// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { portfolioApi } from '@shared/data-access/api/engine/portfolio-api';
import { type SearchMarketplaceFilters } from '@shared/data-access/interfaces/api/engine/citec';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const initialFilters: SearchMarketplaceFilters = {
  risk_level: '',
  client_residency: '',
  esg: '',
  asset_manager: '',
};

export const useModeledPortfolios = () => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<SearchMarketplaceFilters>(initialFilters);

  const payload = useMemo(
    () => Object.fromEntries(Object.entries(filters).filter(([_, value]) => value)),
    [filters],
  );

  const { data, isLoading, isError, isFetching } =
    portfolioApi.useSearchMarketplaceQuery(payload);

  const setFilter = useCallback(
    (key: keyof SearchMarketplaceFilters, value: string) => {
      setFilters(prev => ({ ...prev, [key]: value }));
    },
    [],
  );
  /* istanbul ignore next */
  const marketplaceFilters = [
    {
      key: 'risk_level',
      label: t('research.risk-level', { ns: 'research' }),
      placeholder: t('research.select-risk-level', { ns: 'research' }),
      options: [
        {
          label: t('marketplace.filters.risk-levels.growth', { ns: 'components' }),
          value: 'Growth',
        },
        {
          label: t('marketplace.filters.risk-levels.moderate', {
            ns: 'components',
          }),
          value: 'Moderate',
        },
        {
          label: t('marketplace.filters.risk-levels.conservative', {
            ns: 'components',
          }),
          value: 'Conservative',
        },
        {
          label: t('marketplace.filters.risk-levels.other', { ns: 'components' }),
          value: 'Other',
        },
      ],
      onChange: (value: string) => setFilter('risk_level', value),
      selected: filters.risk_level,
    },
    {
      key: 'client_residency',
      label: t('research.client-residency', { ns: 'research' }),
      placeholder: t('research.select-client-residency', { ns: 'research' }),
      options: [
        {
          label: t('marketplace.filters.client-residency.us-resident', {
            ns: 'components',
          }),
          value: 'US Resident',
        },
        {
          label: t('marketplace.filters.client-residency.non-us-resident', {
            ns: 'components',
          }),
          value: 'Non US Resident',
        },
        {
          label: t('marketplace.filters.client-residency.mexico', {
            ns: 'components',
          }),
          value: 'Mexico',
        },
        {
          label: t('marketplace.filters.client-residency.spain', {
            ns: 'components',
          }),
          value: 'Spain',
        },
        {
          label: t('marketplace.filters.client-residency.uk', { ns: 'components' }),
          value: 'UK',
        },
      ],
      onChange: (value: string) => setFilter('client_residency', value),
      selected: filters.client_residency,
    },
    {
      key: 'esg',
      label: t('research.esg-focus', { ns: 'research' }),
      placeholder: t('research.select-esg-focus', { ns: 'research' }),
      options: [
        {
          label: t('marketplace.filters.esg-focus.yes', { ns: 'components' }),
          value: 'Yes',
        },
        {
          label: t('marketplace.filters.esg-focus.no', { ns: 'components' }),
          value: 'No',
        },
      ],
      onChange: (value: string) => setFilter('esg', value),
      selected: filters.esg,
    },
    {
      key: 'asset_manager',
      label: t('glossary.assets-manager', { ns: 'glossary' }),
      placeholder: t('research.select-asset-manager', { ns: 'research' }),
      options: [
        {
          label: t('marketplace.filters.asset-managers.blackrock', {
            ns: 'components',
          }),
          value: 'BlackRock',
        },
        {
          label: t('marketplace.filters.asset-managers.vanguard', {
            ns: 'components',
          }),
          value: 'Vanguard',
        },
        {
          label: t('marketplace.filters.asset-managers.amundi', {
            ns: 'components',
          }),
          value: 'Amundi',
        },
        {
          label: t('marketplace.filters.asset-managers.allianz', {
            ns: 'components',
          }),
          value: 'Allianz',
        },
        {
          label: t('marketplace.filters.asset-managers.citec-ai', {
            ns: 'components',
          }),
          value: 'Citec AI',
        },
      ],
      onChange: (value: string) => setFilter('asset_manager', value),
      selected: filters.asset_manager,
    },
  ];

  return {
    filters: marketplaceFilters,
    data: data || [],
    isLoading: isLoading || isFetching,
    error: isError,
  };
};
